import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import type { CSSProperties } from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { Gift } from '@sorare/core/src/atoms/icons/Gift';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandS, LabelM } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { getTaskCompetitionColor } from '#src/lib/task/index.tsx';

import { RewardThresholdsGauge } from './RewardThresholdsGauge/index.tsx';
import { ThresholdRewardRow } from './ThresholdRewardRow/index.tsx';
import type { TaskRewards_taskInterface } from './__generated__/index.graphql.ts';

const StyledNeonEdge = styled(NeonEdge)`
  isolation: isolate;
  padding: calc(6 * var(--unit)) var(--double-unit) var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid var(--competition-color);
  background:
    linear-gradient(180deg, rgba(14, 14, 14, 0) 0%, #0e0e0e 60%),
    conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, var(--c-black) 0) 0
      0/5px 5px;
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
`;

const SectionChevron = styled(ChevronDownBold)<{ $expanded?: boolean }>`
  transform: rotate(${({ $expanded }) => ($expanded ? 180 : 0)}deg);
  flex-shrink: 0;
`;

const ThresholdDetailsHeader = styled(ButtonBase)`
  align-items: center;
  display: flex;
  gap: var(--unit);
  z-index: 1;
`;

const ThresholdsList = styled(Vertical).attrs({ gap: 1 })`
  padding: var(--unit) 0;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: var(--c-nd-200);
`;

type Props = {
  task: TaskRewards_taskInterface;
};

export const TaskRewards = ({ task }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const { formatMessage } = useIntl();

  if (
    (!isType(task, 'CardsCountTask') &&
      !isType(task, 'CompleteCollectionsCountTask')) ||
    !task.thresholds?.length
  ) {
    return null;
  }

  const { thresholds, liveScore } = task;
  const competitionColor = getTaskCompetitionColor(
    task,
    'var(--c-set-surface-primary)'
  );
  const currentThresholdIndex = thresholds.findIndex(
    ({ current }) => !!current
  );
  const effectiveIndex =
    currentThresholdIndex === -1 ? 0 : currentThresholdIndex;
  const currentThreshold = thresholds[effectiveIndex];

  return (
    <Vertical
      style={{ '--competition-color': competitionColor } as CSSProperties}
    >
      <Horizontal>
        <Gift size={2} color="var(--c-set-surface-primary)" />
        <BrandS>
          <FormattedMessage {...glossary.rewards} />
        </BrandS>
      </Horizontal>
      <BodyM>
        <FormattedMessage
          defaultMessage="Earn more reward as you complete more {item}!"
          values={{
            item: isType(task, 'CompleteCollectionsCountTask')
              ? formatMessage(navLabels.collections).toLowerCase()
              : formatMessage(glossary.cards).toLowerCase(),
          }}
        />
      </BodyM>
      <StyledNeonEdge
        color={competitionColor}
        side="top"
        spread="50%"
        haloOpacity={0.5}
      >
        <RewardThresholdsGauge
          thresholds={thresholds}
          effectiveIndex={effectiveIndex}
          liveScore={liveScore}
        />
        <Divider />
        <Vertical>
          <ThresholdDetailsHeader onClick={() => setExpanded(prev => !prev)}>
            <SectionChevron $expanded={expanded} />
            <LabelM bold>
              <FormattedMessage {...glossary.rewards} />
            </LabelM>
          </ThresholdDetailsHeader>

          <Collapse
            open={expanded}
            gap={0}
            collapsedContent={
              <ThresholdRewardRow
                threshold={currentThreshold}
                index={currentThresholdIndex}
                currentThresholdIndex={currentThresholdIndex}
                competitionColor={competitionColor}
                isCompleteCollectionsCountTask={isType(
                  task,
                  'CompleteCollectionsCountTask'
                )}
              />
            }
          >
            <ThresholdsList>
              {thresholds.map((threshold, i) => (
                <ThresholdRewardRow
                  key={`${threshold.count}-${i}`}
                  threshold={threshold}
                  index={i}
                  currentThresholdIndex={currentThresholdIndex}
                  competitionColor={competitionColor}
                  isCompleteCollectionsCountTask={isType(
                    task,
                    'CompleteCollectionsCountTask'
                  )}
                />
              ))}
            </ThresholdsList>
          </Collapse>
        </Vertical>
      </StyledNeonEdge>
    </Vertical>
  );
};

TaskRewards.fragments = {
  taskInterface: gql`
    fragment TaskRewards_taskInterface on TaskInterface {
      id
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        competition {
          slug
        }
        thresholds {
          count
          current
          ...ThresholdRewardRow_threshold
        }
      }
      ... on CardsCountTask {
        id
        liveScore
        competition {
          slug
        }
        thresholds {
          count
          current
          ...ThresholdRewardRow_threshold
        }
      }
      ...getTaskCompetitionColor_taskInterface
    }
    ${ThresholdRewardRow.fragments.threshold}
    ${getTaskCompetitionColor.fragments.taskInterface}
  ` as TypedDocumentNode<TaskRewards_taskInterface>,
};
